import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { LeagueAccuracyModal } from '../components/LeagueAccuracyModal';
import { UserMessages } from '../components/UserMessages';
import { ConfidenceBrowser } from '../components/ConfidenceBrowser';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Target,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Trophy,
  BarChart3,
  Activity,
  Calendar,
  Radio,
  ChevronRight,
  User,
  CreditCard,
  Share2,
  RefreshCw,
  Crown,
  Loader2,
  Download,
  AlertTriangle,
  Users,
  Mail,
  Gift,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [standings, setStandings] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState('nba');
  const [loadingStandings, setLoadingStandings] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // League accuracy modal state
  const [accuracyLeague, setAccuracyLeague] = useState(null);
  const [showLeagueModal, setShowLeagueModal] = useState(false);
  const [leagueModalType, setLeagueModalType] = useState('user'); // 'user' or 'platform'
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  
  // Referrals state
  const [myReferrals, setMyReferrals] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);

  // Messages state
  const [showMessages, setShowMessages] = useState(false);

  const openLeagueAccuracy = (leagueId, type = 'user') => {
    setAccuracyLeague(leagueId);
    setLeagueModalType(type);
    setShowLeagueModal(true);
  };

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/billing/subscription-status`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  }, []);

  const fetchMyReferrals = useCallback(async () => {
    setLoadingReferrals(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/my-referrals`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyReferrals(data);
      }
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
    } finally {
      setLoadingReferrals(false);
    }
  }, []);

  const sendInviteEmail = async (userId, userName) => {
    setSendingInvite(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/send-invite/${userId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Failed to send invite');
      }
    } catch (err) {
      toast.error('Failed to send invite');
    } finally {
      setSendingInvite(null);
    }
  };

  const startTrial = async () => {
    setStartingTrial(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/billing/start-trial`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchSubscriptionStatus();
      } else {
        toast.error(data.detail || 'Failed to start trial');
      }
    } catch (err) {
      toast.error('Failed to start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const cancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/billing/cancel-subscription`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Subscription cancelled successfully');
        setShowCancelDialog(false);
        fetchSubscriptionStatus();
      } else {
        toast.error(data.detail || 'Failed to cancel subscription');
      }
    } catch (err) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/dashboard/user`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else if (response.status === 401) {
        navigate('/login');
      }
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  const fetchStandings = useCallback(async (leagueId) => {
    setLoadingStandings(true);
    try {
      const response = await fetch(`${API_URL}/api/standings/${leagueId}`);
      if (response.ok) {
        const data = await response.json();
        setStandings(data);
      }
    } catch (err) {
      console.error('Failed to fetch standings:', err);
    } finally {
      setLoadingStandings(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboard();
    fetchStandings(selectedLeague);
    fetchSubscriptionStatus();
    // Fetch referrals only for subscribers
    if (user?.is_subscriber || subscriptionStatus?.status === 'active') {
      fetchMyReferrals();
    }
  }, [isAuthenticated, navigate, fetchDashboard, fetchStandings, fetchSubscriptionStatus, fetchMyReferrals, user?.is_subscriber, subscriptionStatus?.status, selectedLeague]);

  // Fetch referrals when subscription status changes
  useEffect(() => {
    if (subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'free_grant') {
      fetchMyReferrals();
    }
  }, [subscriptionStatus?.status, fetchMyReferrals]);

  useEffect(() => {
    fetchStandings(selectedLeague);
  }, [selectedLeague, fetchStandings]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
    fetchSubscriptionStatus();
  };

  const exportPredictions = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/dashboard/user/export-predictions`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `predictions_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Predictions exported successfully!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to export predictions');
      }
    } catch (err) {
      toast.error('Failed to export predictions');
    } finally {
      setExporting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'incorrect':
      case 'resolved':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'correct':
        return <Badge className="bg-green-500/20 text-green-400">Correct</Badge>;
      case 'incorrect':
      case 'resolved':
        return <Badge className="bg-red-500/20 text-red-400">Incorrect</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Unable to load dashboard</h2>
            <Button onClick={() => navigate('/board')}>Go to Board</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { user: userData, prediction_stats, recent_predictions, platform_accuracy, games_summary } = dashboardData;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl font-bold uppercase tracking-tight" data-testid="user-dashboard-title">
                My Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Track your predictions and performance
              </p>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              {subscriptionStatus?.is_subscriber && (
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={exportPredictions}
                  disabled={exporting}
                  data-testid="export-predictions-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Data
                </Button>
              )}
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                className="rounded-sm"
                onClick={() => navigate('/board')}
              >
                Make Predictions
              </Button>
            </div>
          </div>

          {/* User Info Card */}
          <Card className="mb-8 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/20">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-8 h-8 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{userData.email}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {userData.role === 'admin' ? 'Admin' : 'Member'}
                      </Badge>
                      {userData.subscription_status === 'active' && (
                        <Badge className="bg-green-500/20 text-green-400 text-xs">
                          Pro Subscriber
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold font-mono">{userData.credits?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground">Credits</p>
                  </div>
                  {userData.referral_code && (
                    <div className="text-center">
                      <p className="text-lg font-mono">{userData.referral_code}</p>
                      <p className="text-xs text-muted-foreground">Referral Code</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Management Card */}
          <Card className="mb-8 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20" data-testid="subscription-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Subscription
              </CardTitle>
              <CardDescription>Manage your premium subscription</CardDescription>
            </CardHeader>
            <CardContent>
              {subscriptionStatus ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Status</p>
                      <Badge className={
                        subscriptionStatus.status === 'free_grant' ? 'bg-yellow-500/20 text-yellow-400' :
                        subscriptionStatus.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        subscriptionStatus.status === 'trialing' ? 'bg-blue-500/20 text-blue-400' :
                        subscriptionStatus.status === 'cancelled' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }>
                        {subscriptionStatus.status === 'free_grant' ? 'Free Account' :
                         subscriptionStatus.status === 'trialing' ? 'Free Trial' :
                         subscriptionStatus.status === 'active' ? 'Active' :
                         subscriptionStatus.status === 'cancelled' ? 'Cancelled' :
                         subscriptionStatus.status === 'expired' ? 'Expired' : 'Inactive'}
                      </Badge>
                    </div>
                    {subscriptionStatus.status === 'free_grant' && (
                      <div className="text-right">
                        <p className="text-sm text-yellow-400 font-medium">Granted by Admin</p>
                        <p className="text-xs text-muted-foreground">No expiration</p>
                      </div>
                    )}
                    {subscriptionStatus.status === 'trialing' && subscriptionStatus.trial_days_remaining !== undefined && (
                      <div className="text-right">
                        <p className="text-2xl font-bold font-mono text-blue-400">
                          {subscriptionStatus.trial_days_remaining}
                        </p>
                        <p className="text-xs text-muted-foreground">days remaining</p>
                      </div>
                    )}
                    {subscriptionStatus.status === 'active' && subscriptionStatus.days_remaining !== undefined && (
                      <div className="text-right">
                        <p className="text-2xl font-bold font-mono text-green-400">
                          {subscriptionStatus.days_remaining}
                        </p>
                        <p className="text-xs text-muted-foreground">days until renewal</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    {subscriptionStatus.can_start_trial && (
                      <Button 
                        onClick={startTrial}
                        disabled={startingTrial}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-sm"
                        data-testid="start-trial-btn"
                      >
                        {startingTrial ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Crown className="w-4 h-4 mr-2" />
                        )}
                        Start {subscriptionStatus.trial_days_available}-Day Free Trial
                      </Button>
                    )}
                    
                    {!subscriptionStatus.is_subscriber && !subscriptionStatus.can_start_trial && (
                      <Button 
                        onClick={() => navigate('/billing')}
                        className="flex-1 btn-blaze rounded-sm"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </Button>
                    )}
                    
                    {subscriptionStatus.is_subscriber && subscriptionStatus.status !== 'cancelled' && subscriptionStatus.status !== 'free_grant' && (
                      <Button 
                        onClick={() => setShowCancelDialog(true)}
                        disabled={cancellingSubscription}
                        variant="outline"
                        className="rounded-sm text-red-400 border-red-400/20 hover:bg-red-400/10"
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                  
                  {subscriptionStatus.status === 'trialing' && (
                    <p className="text-xs text-muted-foreground">
                      After your trial ends, you'll be charged automatically unless you cancel.
                    </p>
                  )}
                  
                  {subscriptionStatus.status === 'free_grant' && (
                    <p className="text-xs text-yellow-400/80">
                      You have a free account with full subscriber access.
                    </p>
                  )}
                </div>
              ) : (
                <Skeleton className="h-24 w-full" />
              )}
            </CardContent>
          </Card>

          {/* My Referrals Card - Only for subscribers */}
          {(subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'free_grant' || subscriptionStatus?.status === 'trialing') && (
            <Card className="mb-8 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20" data-testid="referrals-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-green-400" />
                  My Referrals
                </CardTitle>
                <CardDescription>
                  {myReferrals ? (
                    <>Earn {myReferrals.referrer_credit_percent}% credit for each friend who subscribes</>
                  ) : (
                    'Track your referrals and earn credits'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingReferrals ? (
                  <Skeleton className="h-24 w-full" />
                ) : myReferrals ? (
                  <div className="space-y-4">
                    {/* Referral Stats */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 rounded-lg bg-background/50">
                        <p className="text-2xl font-bold font-mono text-green-400">{myReferrals.total_referred}</p>
                        <p className="text-xs text-muted-foreground">Total Referred</p>
                      </div>
                      <div className="p-3 rounded-lg bg-background/50">
                        <p className="text-2xl font-bold font-mono text-emerald-400">{myReferrals.subscribed_count}</p>
                        <p className="text-xs text-muted-foreground">Subscribed</p>
                      </div>
                      <div className="p-3 rounded-lg bg-background/50">
                        <p className="text-2xl font-bold font-mono text-yellow-400">{myReferrals.pending_count}</p>
                        <p className="text-xs text-muted-foreground">Pending</p>
                      </div>
                    </div>
                    
                    {/* Referred Users List */}
                    {myReferrals.referred_users?.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">Your Referrals</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {myReferrals.referred_users.map((ref) => (
                            <div key={ref.user_id} className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{ref.name || 'Anonymous'}</p>
                                  <p className="text-xs text-muted-foreground">{ref.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {ref.is_subscribed ? (
                                  <Badge className="bg-green-500/20 text-green-400">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Subscribed
                                  </Badge>
                                ) : (
                                  <>
                                    <Badge className="bg-yellow-500/20 text-yellow-400">
                                      <Clock className="w-3 h-3 mr-1" />
                                      Pending
                                    </Badge>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/20"
                                      onClick={() => sendInviteEmail(ref.user_id, ref.name)}
                                      disabled={sendingInvite === ref.user_id}
                                      data-testid={`send-invite-${ref.user_id}`}
                                    >
                                      {sendingInvite === ref.user_id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <>
                                          <Mail className="w-3 h-3 mr-1" />
                                          Invite
                                        </>
                                      )}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-sm text-muted-foreground">No referrals yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Share your referral code to start earning!</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 border-green-500/30 text-green-400 hover:bg-green-500/20"
                          onClick={() => navigate('/referral')}
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Go to Referral Page
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">Unable to load referral data</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={fetchMyReferrals}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Predictions */}
            <Card data-testid="stat-total-predictions">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Predictions
                </CardTitle>
                <Target className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono">{prediction_stats.total}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {prediction_stats.pending} pending
                </p>
              </CardContent>
            </Card>

            {/* Accuracy */}
            <Card data-testid="stat-accuracy">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Your Accuracy
                </CardTitle>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold font-mono ${
                  prediction_stats.accuracy >= 50 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {prediction_stats.accuracy}%
                </div>
                <Progress 
                  value={prediction_stats.accuracy} 
                  className="h-2 mt-2"
                />
              </CardContent>
            </Card>

            {/* Correct Predictions */}
            <Card data-testid="stat-correct">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Correct
                </CardTitle>
                <CheckCircle2 className="w-4 h-4 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono text-green-400">
                  {prediction_stats.correct}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {prediction_stats.incorrect} incorrect
                </p>
              </CardContent>
            </Card>

            {/* Platform Comparison */}
            <Card data-testid="stat-platform">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Platform Accuracy
                </CardTitle>
                <BarChart3 className="w-4 h-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono text-purple-400">
                  {platform_accuracy.overall}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {platform_accuracy.total_predictions} predictions
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Accuracy by League Section */}
          {(prediction_stats.by_league && Object.keys(prediction_stats.by_league).length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Your Accuracy by League */}
              <Card data-testid="your-accuracy-by-league">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                    Your Accuracy by League
                  </CardTitle>
                  <CardDescription>Click a league to see detailed breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {Object.entries(prediction_stats.by_league).map(([league, data]) => (
                      <button
                        key={league}
                        onClick={() => openLeagueAccuracy(league, 'user')}
                        className="p-3 bg-secondary/30 rounded-sm text-center hover:bg-secondary/50 transition-colors cursor-pointer group"
                        data-testid={`user-league-accuracy-${league}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-muted-foreground uppercase">{league.toUpperCase()}</p>
                          <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className={`text-xl font-bold font-mono ${
                          data.accuracy >= 60 ? 'text-green-400' : 
                          data.accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {data.accuracy}%
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {data.correct}/{data.total}
                        </p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Platform Accuracy by League */}
              {platform_accuracy.by_league && Object.keys(platform_accuracy.by_league).length > 0 && (
                <Card data-testid="platform-accuracy-by-league">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-purple-400" />
                      Platform Accuracy by League
                    </CardTitle>
                    <CardDescription>Click a league to see game predictions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {Object.entries(platform_accuracy.by_league).map(([league, data]) => (
                        <button
                          key={league}
                          onClick={() => openLeagueAccuracy(league, 'platform')}
                          className="p-3 bg-secondary/30 rounded-sm text-center hover:bg-secondary/50 transition-colors cursor-pointer group relative"
                          data-testid={`platform-league-accuracy-${league}`}
                        >
                          {/* MLB Spring Training Badge */}
                          {league === 'mlb' && data.total === 0 && (
                            <div className="absolute -top-2 -right-2 bg-amber-500/20 text-amber-400 text-[10px] px-2 py-0.5 rounded-full border border-amber-500/30">
                              Spring Training
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs text-muted-foreground uppercase">{league.toUpperCase()}</p>
                            <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          {league === 'mlb' && data.total === 0 ? (
                            <>
                              <p className="text-base font-medium text-amber-400">
                                Starts Apr '26
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Click for details
                              </p>
                            </>
                          ) : (
                            <>
                              <p className={`text-xl font-bold font-mono ${
                                data.accuracy >= 60 ? 'text-green-400' : 
                                data.accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {data.accuracy}%
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {data.correct}/{data.total}
                              </p>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Recent Predictions */}
            <div className="lg:col-span-2">
              <Card data-testid="recent-predictions-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-purple-400" />
                        Recent Predictions
                      </CardTitle>
                      <CardDescription>Your latest game predictions</CardDescription>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/prediction-history')}
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {recent_predictions && recent_predictions.length > 0 ? (
                    <div className="space-y-4">
                      {recent_predictions.map((prediction, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-4 bg-secondary/30 rounded-sm hover:bg-secondary/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/game/${prediction.game_id}`)}
                        >
                          <div className="flex items-center gap-4">
                            {getStatusIcon(prediction.status)}
                            <div>
                              <p className="font-medium">
                                {prediction.away_team} @ {prediction.home_team}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Picked: {prediction.predicted_winner === 'home' 
                                  ? prediction.home_team 
                                  : prediction.predicted_winner === 'away' 
                                    ? prediction.away_team 
                                    : 'TIE'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {prediction.game_status === 'final' && (
                              <span className="text-sm font-mono">
                                {prediction.away_score} - {prediction.home_score}
                              </span>
                            )}
                            {getStatusBadge(prediction.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-medium mb-2">No predictions yet</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Start making predictions to see them here
                      </p>
                      <Button onClick={() => navigate('/board')} className="rounded-sm">
                        Browse Games
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Games Summary */}
              <Card data-testid="games-summary-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    Games Today
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div className="flex items-center gap-3">
                        <Radio className="w-5 h-5 text-red-400 animate-pulse" />
                        <span>Live Now</span>
                      </div>
                      <span className="text-2xl font-bold font-mono">{games_summary.live}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-yellow-400" />
                        <span>Upcoming</span>
                      </div>
                      <span className="text-2xl font-bold font-mono">{games_summary.upcoming}</span>
                    </div>
                  </div>
                  <Button 
                    className="w-full mt-4 rounded-sm"
                    variant="outline"
                    onClick={() => navigate('/board')}
                  >
                    View All Games
                  </Button>
                </CardContent>
              </Card>

              {/* Team Standings */}
              <Card data-testid="standings-card">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-400" />
                      Standings
                    </div>
                    <select 
                      value={selectedLeague}
                      onChange={(e) => setSelectedLeague(e.target.value)}
                      className="bg-secondary border border-border rounded-sm px-2 py-1 text-sm"
                    >
                      <option value="nba">NBA</option>
                      <option value="nhl">NHL</option>
                      <option value="nfl">NFL</option>
                      <option value="mlb">MLB</option>
                      <option value="mls">MLS</option>
                      <option value="epl">EPL</option>
                    </select>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingStandings ? (
                    <div className="space-y-2">
                      {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8" />)}
                    </div>
                  ) : standings?.standings?.length > 0 ? (
                    <div className="space-y-1">
                      <div className="grid grid-cols-4 text-xs text-muted-foreground pb-1 border-b border-border">
                        <span>#</span>
                        <span>Team</span>
                        <span className="text-center">W-L</span>
                        <span className="text-right">%</span>
                      </div>
                      {standings.standings.slice(0, 10).map((team, idx) => (
                        <div key={team.team_id} className="grid grid-cols-4 items-center py-1 text-sm hover:bg-secondary/30 rounded-sm">
                          <span className="text-muted-foreground">{idx + 1}</span>
                          <span className="font-medium truncate">{team.abbreviation}</span>
                          <span className="text-center font-mono">{team.wins}-{team.losses}</span>
                          <span className="text-right font-mono text-green-400">{team.win_pct}%</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No standings available</p>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start rounded-sm"
                    onClick={() => navigate('/predictions')}
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    View Leaderboard
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start rounded-sm"
                    onClick={() => navigate('/player-stats')}
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Player Statistics
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start rounded-sm"
                    onClick={() => navigate('/referral')}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Refer a Friend
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start rounded-sm"
                    onClick={() => navigate('/billing')}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start rounded-sm border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => setShowMessages(!showMessages)}
                    data-testid="contact-support-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </CardContent>
              </Card>

              {/* Messages Section (Expandable) */}
              {showMessages && (
                <Card className="bg-card/50 border-primary/20" data-testid="user-messages-section">
                  <CardContent className="py-4">
                    <UserMessages />
                  </CardContent>
                </Card>
              )}

              {/* Performance Tips */}
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-400">
                    <TrendingUp className="w-5 h-5" />
                    Performance Tip
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {prediction_stats.accuracy >= 60 
                      ? "Great job! Your accuracy is above average. Keep analyzing the trends and injuries data!"
                      : prediction_stats.accuracy >= 40
                        ? "You're doing well! Check the Confidence Score tab on game pages for better insights."
                        : "Check the Trends and Schedule tabs on game pages to make more informed predictions!"
                    }
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Confidence Browser Section - Available to all users */}
          <div className="mt-8">
            <ConfidenceBrowser isAdmin={false} />
          </div>
        </div>
      </main>

      <Footer />
      
      {/* League Accuracy Detail Modal */}
      <LeagueAccuracyModal
        isOpen={showLeagueModal}
        onClose={() => setShowLeagueModal(false)}
        leagueId={accuracyLeague}
        leagueName={accuracyLeague?.toUpperCase()}
        isPlatform={leagueModalType === 'platform'}
      />
      
      {/* Cancel Subscription Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Cancel Subscription
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={cancelSubscription}
              disabled={cancellingSubscription}
              className="bg-red-500 hover:bg-red-600 rounded-sm"
            >
              {cancellingSubscription ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
